// Una imagen o video del lienzo, SUELTA (ADR-2026-09-16-las-fotos-del-lienzo-van-sueltas):
// la foto es el nodo, sin marco. Su rótulo —el nombre del producto o de la modelo
// (ADR-2026-09-16-el-titulo-de-la-foto-es-el-producto)— asoma DEBAJO al pasar el mouse o
// con la foto seleccionada, en la misma capa que la barra de acciones (`NodeToolbar`),
// que React Flow NO escala con el zoom: así mide lo mismo que las opciones de arriba a
// cualquier zoom y no ocupa lugar en el acomodo. Mientras no llegó, el MISMO recuadro de
// carga que la columna de resultados (`RecuadroGenerando`, con el reloj de la tanda).
// Con la celda seleccionada —y sola— la barra de acciones flota arriba, despegada de la
// foto, y entra y sale con una transición corta (se la deja montada mientras sale:
// `NodeToolbar` desmonta al instante con `isVisible=false`); mientras se arrastra la
// foto se esconde, y con varias seleccionadas manda la barra de selección del pie.
// Con la barra ya a la vista, otro click sobre la foto (o un doble click) abre el visor
// (ADR-2026-09-16-la-barra-del-lienzo-muestra-carrusel-y-animar-2).
//
// Al cargar la imagen se reporta su proporción real: el acomodo dibuja cada foto entera,
// con su forma, y no con la del formato pedido (que en un video es una apuesta).

import { memo, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { NodeToolbar, Position, type Node, type NodeProps } from '@xyflow/react';
import { Play } from 'lucide-react';
import CachedImg from '../../components/CachedImg';
import { useExitTransition } from '../../hooks/useExitTransition';
import { RecuadroGenerando } from '../../pages/v2/components/PanelTandaCreaciones';
import { CapaVerificacion } from '../../pages/v2/verificacion/CapaVerificacion';
import { anchoDeCelda, type DatosCelda } from './acomodo';
import { BarraDeCelda } from './BarraDeCelda';
import { LienzoContext } from './lienzoContext';
import { PieDeCelda } from './PieDeCelda';

type NodoDeCelda = Node<DatosCelda, 'celda'>;

/** Aire entre la foto y su barra de acciones. */
const SEPARACION_BARRA = 18;
/** Lo que dura la salida de la barra (la entrada dura lo mismo). */
const MS_BARRA = 200;
/** Cuánto sigue a la vista el pie después de que el puntero sale de la foto: lo que
 *  tarda uno en bajar al chevron. El pie está en otra capa (portal), así que salir de
 *  la foto para tocarlo cuenta como "salir"; sin esta gracia se iba antes del click. */
const GRACIA_HOVER_MS = 450;
export const NodoCelda = memo(function NodoCelda({ id, data, selected, dragging }: NodeProps<NodoDeCelda>) {
  const lienzo = useContext(LienzoContext);
  const [hover, setHover] = useState(false);
  const salidaRef = useRef<number | null>(null);
  const entrar = useCallback(() => {
    if (salidaRef.current) window.clearTimeout(salidaRef.current);
    salidaRef.current = null;
    setHover(true);
  }, []);
  const salir = useCallback(() => {
    if (salidaRef.current) window.clearTimeout(salidaRef.current);
    salidaRef.current = window.setTimeout(() => setHover(false), GRACIA_HOVER_MS);
  }, []);
  useEffect(() => () => {
    if (salidaRef.current) window.clearTimeout(salidaRef.current);
  }, []);
  const alHoverDelPie = useCallback((dentro: boolean) => (dentro ? entrar() : salir()), [entrar, salir]);
  const [infoAbierta, setInfoAbierta] = useState(false);
  const mini = data.imageId ? lienzo.miniaturas.get(data.imageId) : undefined;
  const pieVisible = (hover || !!selected) && !lienzo.sinPie?.(id);
  // Con fila (y datos) es accionable como siempre; una propia con `url` sólo si hay una
  // barra prestada que sepa qué hacer con ella.
  const accionable = !!lienzo.companyId && (!!mini?.datos || (!!data.url && !!lienzo.Barra));
  // La única accionable de la selección (y no `seleccionadas.length`, que no cuenta las
  // propias del modo Diseño) — ADR-2026-09-17-la-barra-cuenta-lo-accionable.
  const barraVisible = !!selected && lienzo.seleccionAccionable === 1 && accionable && !dragging;
  const { mounted: barraMontada, animated: barraAnimada } = useExitTransition(barraVisible, MS_BARRA);
  // Si la barra YA estaba a la vista cuando el puntero bajó, el click abre el visor. Se
  // mira al bajar y no al soltar porque React Flow selecciona el nodo en el pointerdown:
  // para cuando llega el click, `selected` ya es true aunque recién se lo esté tocando.
  // Un doble click cae solo: el primero selecciona, el segundo ya encuentra la barra.
  // Después de un arrastre no hay click (d3-drag lo suprime), así que no abre de más.
  // Sólo cuentan los eventos que nacen EN la foto: la barra y el pie viven en portales
  // (`NodeToolbar`) y los eventos de React suben por el árbol de React, no por el DOM,
  // así que un click en un botón de la barra llegaba acá y abría el visor de más.
  const barraAbiertaAlTocarRef = useRef(false);
  const alBajarPuntero = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!e.currentTarget.contains(e.target as HTMLElement)) return;
      barraAbiertaAlTocarRef.current = barraVisible;
    },
    [barraVisible],
  );
  // Deseleccionar la foto cierra su Info: el pie se va con ella.
  useEffect(() => {
    if (!selected) setInfoAbierta(false);
  }, [selected]);
  if (!data.imageId && !data.url) {
    return (
      <div
        className="h-full w-full"
        onMouseEnter={entrar}
        onMouseLeave={salir}
      >
        <RecuadroGenerando
          ratio={data.ratio}
          creadaEn={data.creadaEn || Date.now()}
          esVideo={data.esVideo}
        />
        <PieDeCelda
          titulo={data.titulo}
          ancho={anchoDeCelda(data.ratio)}
          visible={pieVisible}
          celda={null}
          abierta={false}
          onAbrir={() => {}}
          onHover={alHoverDelPie}
        />
      </div>
    );
  }
  const imageId = data.imageId ?? '';
  // Sin `companyId` no hay acción posible (todo path de Storage lo lleva): la celda no es
  // accionable y la barra prestada recibe `null`, igual que `accionable` más arriba.
  const celdaAccionable =
    mini?.datos && data.imageId && lienzo.companyId
      ? {
          imageId,
          companyId: lienzo.companyId ?? '',
          esVideo: data.esVideo,
          src: mini.src,
          datos: mini.datos,
          titulo: data.titulo,
        }
      : null;
  const src = mini?.src ?? data.url;
  return (
    <div
      className={`relative h-full w-full overflow-hidden rounded-2xl bg-[#E9EAF0] transition-transform duration-150 ${
        selected ? 'ring-2 ring-[#1644EB] ring-offset-2 ring-offset-[#F3F4F8]' : ''
      } ${barraVisible ? 'active:scale-[0.98]' : ''}`}
      // Con la barra a la vista la foto es un botón (abre el visor): manito, y al
      // apretarla se achica un poco, como el resto de los botones de la app. Inline
      // porque `lienzo.css` pone la flecha sobre todo nodo con más especificidad.
      style={barraVisible ? { cursor: 'var(--cursor-manito)' } : undefined}
      onMouseEnter={entrar}
      onMouseLeave={salir}
      onPointerDownCapture={alBajarPuntero}
      onClick={(e) => {
        if (!e.currentTarget.contains(e.target as HTMLElement)) return;
        if (lienzo.alTocarCelda?.(id)) return;
        if (barraAbiertaAlTocarRef.current && barraVisible && data.imageId) lienzo.abrir(data.imageId);
      }}
    >
      {src ? (
        <CachedImg
          src={src}
          alt={data.titulo}
          className="h-full w-full object-cover"
          onLoad={(e) => {
            const img = e.currentTarget;
            if (img.naturalWidth > 0 && img.naturalHeight > 0) {
              // Por id de CELDA (no de imagen): una fila usa su `imageId` como id, así
              // que esto cubre las dos (ADR-2026-09-17-el-lienzo-presta-tres-ranuras).
              lienzo.reportarRatio(id, img.naturalWidth / img.naturalHeight);
            }
          }}
        />
      ) : (
        <span className="block h-full w-full animate-pulse" />
      )}
      {data.esVideo && (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-sm">
            <Play className="ml-0.5 h-4 w-4" fill="currentColor" />
          </span>
        </span>
      )}
      {/* La verificación de calidad, con la función prendida
          (ADR-2026-09-20-la-verificacion-de-calidad-llega-al-lienzo): la MISMA capa que
          la columna de resultados —velo de "analizando", pastilla arriba a la derecha
          que abre el popover— pero SIN el botón sobre la foto: "Verificar" va en la
          barra, a la derecha de "Ver"
          (ADR-2026-09-21-verificar-va-en-la-barra-de-la-celda). `contents`: la capa se
          posiciona contra la foto, no contra este envoltorio. `nodrag nopan` y el corte
          del click: tocar el botón o la pastilla no arrastra la foto ni abre el visor. */}
      {lienzo.verificacion && data.imageId && !data.esVideo && (
        <div
          className="contents nodrag nopan"
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          <CapaVerificacion id={data.imageId} api={lienzo.verificacion} sinBoton />
        </div>
      )}
      {lienzo.Adorno && <lienzo.Adorno id={id} ratio={data.ratio} />}
      <PieDeCelda
        titulo={data.titulo}
        ancho={anchoDeCelda(data.ratio)}
        visible={pieVisible}
        celda={celdaAccionable}
        abierta={infoAbierta}
        onAbrir={setInfoAbierta}
        onHover={alHoverDelPie}
      />
      <NodeToolbar isVisible={barraMontada} position={Position.Top} offset={SEPARACION_BARRA}>
        {lienzo.Barra ? (
          <lienzo.Barra id={id} datos={data} celda={celdaAccionable} animada={barraAnimada} />
        ) : (
          celdaAccionable && <BarraDeCelda celda={celdaAccionable} animada={barraAnimada} nodoId={id} />
        )}
      </NodeToolbar>
    </div>
  );
});
